/**
 * Auth context — wraps Amplify v6 Cognito auth for the rest of the app.
 *
 * Exposes:
 *   useAuth() → {
 *     status: 'loading' | 'signedOut' | 'signedIn',
 *     user,                         // { username, email } or null
 *     signIn(email, password),
 *     completeNewPassword(pw),      // used when Cognito requires a permanent password
 *     signOut(),
 *   }
 *
 * On mount, the provider checks for an existing session (AsyncStorage-backed
 * via cognitoUserPoolsTokenProvider). While that check is in flight, status is
 * 'loading' — RootNavigator shows a splash to avoid flashing the login screen.
 */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  getCurrentUser,
  fetchUserAttributes,
  confirmSignIn,
} from 'aws-amplify/auth';

import { configureAmplify, isCognitoConfigured } from './authConfig';

configureAmplify();

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [status, setStatus] = useState('loading');
  const [user, setUser]     = useState(null);
  // Tracks the in-flight challenge (e.g. NEW_PASSWORD_REQUIRED) so we can
  // complete it on the next call without the caller passing state around.
  const pendingChallenge = useRef(null);

  const refreshUser = useCallback(async () => {
    // With placeholder Cognito config the SDK would issue a fetch to a
    // bogus endpoint and fail with an opaque CORS-shaped error. Skip the
    // probe entirely — the LoginScreen will surface the misconfiguration.
    if (!isCognitoConfigured()) {
      setUser(null);
      setStatus('signedOut');
      return;
    }
    try {
      const current = await getCurrentUser();
      let email = current.signInDetails?.loginId || null;
      try {
        const attrs = await fetchUserAttributes();
        if (attrs.email) email = attrs.email;
      } catch {/* attribute fetch is optional */}
      setUser({ username: current.username, email });
      setStatus('signedIn');
    } catch {
      setUser(null);
      setStatus('signedOut');
    }
  }, []);

  useEffect(() => { refreshUser(); }, [refreshUser]);

  const signIn = useCallback(async (email, password) => {
    if (!isCognitoConfigured()) {
      return {
        ok: false,
        code: 'ConfigError',
        message: 'Cognito is not configured. Set COGNITO_USER_POOL_ID and COGNITO_USER_POOL_CLIENT_ID (or edit src/lib/authConfig.js).',
      };
    }
    try {
      const res = await amplifySignIn({
        username: email.trim().toLowerCase(),
        password,
      });
      if (res.isSignedIn) {
        pendingChallenge.current = null;
        await refreshUser();
        return { ok: true };
      }
      pendingChallenge.current = res.nextStep?.signInStep || null;
      return {
        ok: false,
        code: 'CHALLENGE',
        nextStep: res.nextStep?.signInStep,
        message: humanizeChallenge(res.nextStep?.signInStep),
      };
    } catch (err) {
      return {
        ok: false,
        code: err?.name || 'Error',
        message: humanizeError(err),
      };
    }
  }, [refreshUser]);

  const completeNewPassword = useCallback(async (newPassword) => {
    try {
      const res = await confirmSignIn({ challengeResponse: newPassword });
      if (res.isSignedIn) {
        pendingChallenge.current = null;
        await refreshUser();
        return { ok: true };
      }
      pendingChallenge.current = res.nextStep?.signInStep || null;
      return {
        ok: false,
        code: 'CHALLENGE',
        nextStep: res.nextStep?.signInStep,
        message: humanizeChallenge(res.nextStep?.signInStep),
      };
    } catch (err) {
      return { ok: false, code: err?.name || 'Error', message: humanizeError(err) };
    }
  }, [refreshUser]);

  const signOut = useCallback(async () => {
    try { await amplifySignOut(); } catch {/* ignore */}
    pendingChallenge.current = null;
    setUser(null);
    setStatus('signedOut');
  }, []);

  const value = {
    status,
    user,
    signIn,
    completeNewPassword,
    signOut,
    pendingChallenge: pendingChallenge.current,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

function humanizeChallenge(step) {
  switch (step) {
    case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
      return 'Set a new password to finish signing in.';
    case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
    case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
      return 'Multi-factor verification is required.';
    case 'CONFIRM_SIGN_UP':
      return 'Your account is not yet confirmed.';
    case 'RESET_PASSWORD':
      return 'Your password must be reset before signing in.';
    default:
      return step ? `Additional step required: ${step}` : 'Additional verification required.';
  }
}

function humanizeError(err) {
  const name = err?.name || '';
  const msg  = err?.message || 'Sign-in failed.';
  switch (name) {
    case 'NotAuthorizedException':   return 'Incorrect email or password.';
    case 'UserNotFoundException':    return 'No account found for that email.';
    case 'InvalidParameterException':return 'Enter a valid email address.';
    case 'UserNotConfirmedException':return 'Account not confirmed. Contact an admin.';
    case 'PasswordResetRequiredException': return 'Password reset required.';
    case 'NetworkError':             return 'Network error. Check your connection.';
    default: return msg;
  }
}
